/**
 * Segmented del Design System — fuente única del look.
 *
 * Track #F4F4F5 (`--muted`, documentado en app.css como "track de segmented")
 * con padding 2px y radio 9. Ítem activo: fondo blanco, peso 600 y sombra baja
 * (`--shadow-segment`). Inactivo: #71717A sin fondo.
 *
 * Dos componentes lo consumen, y la diferencia es semántica, no visual:
 *
 * - `tabs.tsx` — cuando elegir una opción cambia el PANEL visible (Radix marca
 *   el activo con `data-state=active` y expone role=tab/aria-controls).
 * - `toggle-group.tsx` — cuando filtra o cambia la vista de la MISMA pantalla,
 *   sin panel asociado (Radix marca el activo con `data-state=on` y expone
 *   semántica de radiogroup, que es la correcta para un filtro).
 *
 * Las clases viven acá porque hasta hoy sólo Tabs tenía el look: el ToggleGroup
 * usaba `variant="outline"` con `bg-accent` de activo, que sobre un grupo con
 * bordes unidos no se distinguía del inactivo. Compartir el string es lo que
 * impide que vuelvan a divergir.
 *
 * El estado activo NO se puede armar por template literal: Tailwind escanea el
 * código fuente buscando clases literales, así que `data-[state=${x}]:...` no
 * generaría ninguna regla. Por eso hay una constante por cada data-attr.
 */

/** Riel del control. Envuelve a los ítems y les da el contraste de fondo. */
export const segmentedTrack =
  "bg-muted inline-flex w-fit items-center justify-center gap-[2px] rounded-[9px] p-[2px]";

/**
 * Ítem en reposo (inactivo). El activo se agrega con la constante que
 * corresponda. El `hover:text-foreground` es el único agregado sobre lo que
 * Tabs ya hacía: sin él el inactivo no acusa recibo del mouse, y era la mitad
 * del problema de "no se ve cuál se puede elegir". Sobre el activo no cambia
 * nada, porque ya está en `text-foreground`.
 */
export const segmentedItem =
  "text-muted-foreground inline-flex h-[27px] items-center justify-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium whitespace-nowrap transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

/** Activo de Tabs (`data-state=active`). */
export const segmentedItemActivoTab =
  "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-[var(--shadow-segment)]";

/** Activo de ToggleGroup (`data-state=on`). */
export const segmentedItemActivoToggle =
  "data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:font-semibold data-[state=on]:shadow-[var(--shadow-segment)]";

/**
 * Contador que acompaña al texto del ítem ("Pendientes 12"). Geist Mono y
 * discreto, para que el número no compita con la etiqueta. Sin margen: el
 * ítem ya separa con `gap-1.5`.
 */
export const segmentedCount = "fc-num text-[10.5px] opacity-65";

/* ── Variante "ficha": tabs de nivel superior (DS §4.4bis) ────────────────── */

/**
 * El MISMO control, con el activo en navy en vez de blanco.
 *
 * Cuándo usar cuál — la diferencia es de alcance, no de estética:
 *
 * - variante por defecto (activo blanco) → el segmented ACOTA una lista que
 *   sigue siendo la misma: `Todos / Activos / Inactivos / Papelera`.
 * - variante `ficha` (activo navy) → las pestañas cambian TODO el contenido de
 *   la pantalla: `Información / Documentos` en la ficha de un equipo.
 *
 * El activo va oscuro por una razón medible y no por gusto: el track es
 * `#F4F4F5` y el lienzo de la app es `#F2F4F6` (dos puntos de diferencia), así
 * que un activo BLANCO sobre ese fondo apenas se despega — funciona cuando el
 * control está dentro de una tarjeta blanca, y se pierde cuando está suelto
 * sobre el lienzo, que es justo donde viven las pestañas de una ficha.
 *
 * Todo lo demás (track, mecánica, accesibilidad de Radix) es idéntico: si algún
 * día cambia el riel, cambia para las dos.
 */

/** Riel de la variante ficha: mismo gris, un pelo más de aire y radio 10. */
export const segmentedTrackFicha =
  "bg-muted inline-flex w-fit items-center justify-center gap-[3px] rounded-[10px] p-[3px]";

/** Ítem en reposo de la variante ficha: 30px de alto, radio 8, texto 13px. */
export const segmentedItemFicha =
  "text-muted-foreground inline-flex h-[30px] items-center justify-center gap-1.5 rounded-lg px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

/**
 * Activo de la variante ficha (`data-state=active`). La sombra es la del
 * segmented pero un punto más marcada (`.14` contra `.08`): sobre navy la
 * sombra baja del activo blanco no se percibe.
 *
 * Igual que en el resto del archivo, no se puede armar por template literal —
 * Tailwind escanea clases literales.
 */
export const segmentedItemActivoFicha =
  "data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-[0_1px_2px_rgba(9,9,11,.14)]";
